import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Building, Plus, UserPlus, KeyRound, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency, formatDateOnly } from "@/lib/cn";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";
import { deriveUnitCode } from "@costadorada/shared";
import { ProvisionOwnersModal } from "./ProvisionOwnersModal";
import { ForceDeleteDialog, isForceable, type Dependants } from "@/components/ForceDeleteDialog";

interface Unit {
  id: string;
  unitNumber: string;
  /** Standard visible code (CD1-E-01), derived from the number unless overridden. */
  code: string | null;
  /** USPS mailing address (shown as "Dirección USPS"). */
  officialAddress: string | null;
  building: string | null;
  floor: string | null;
  bedrooms: number | null;
  /** Monthly maintenance fee (USD). Prisma Decimal serializes as a string. */
  monthlyFee: string | null;
  ownerName: string;
  ownerEmail: string | null;
  ownerPhone: string | null;
  ownerEmail2?: string | null;
  ownerPhone2?: string | null;
  isOccupied: boolean;
  /** Active portal users linked to the unit (from GET /units). */
  _count?: { users: number };
  /** The tenant living there now, when the unit is rented. */
  currentTenant?: Tenant | null;
}

interface Tenant {
  id: string;
  name: string;
  email?: string | null;
  email2?: string | null;
  phone?: string | null;
  phone2?: string | null;
  notes?: string | null;
  startDate: string;
  endDate?: string | null;
}

export function UnitsPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Unit | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [provisioning, setProvisioning] = useState(false);

  const { data: units = [], isLoading, isError } = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api.get<Unit[]>("/units")).data,
  });

  const needle = search.trim().toLowerCase();
  const visible = needle
    ? units.filter((u) =>
        [u.unitNumber, u.code ?? "", u.building ?? "", u.ownerName, u.ownerEmail ?? "", u.ownerPhone ?? "", u.ownerEmail2 ?? "", u.ownerPhone2 ?? "", u.officialAddress ?? "", u.currentTenant?.name ?? "", u.currentTenant?.email ?? "", u.currentTenant?.email2 ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
    : units;
  const withoutUser = units.filter((u) => u._count && u._count.users === 0).length;
  // Bulk fee generation skips these, so the count is a to-do for the Administration.
  const withoutFee = units.filter((u) => u.monthlyFee == null || parseFloat(u.monthlyFee) <= 0).length;

  // A unit that still has fees or permits is refused (409 HAS_DEPENDANTS); the admin
  // may then override through a second, explicit confirmation that sends ?force=true.
  const [forceTarget, setForceTarget] = useState<{ id: string; dependants: Dependants } | null>(null);
  const deleteMutation = useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }) =>
      (await api.delete(`/units/${id}${force ? "?force=true" : ""}`)).data,
    onSuccess: () => {
      setForceTarget(null);
      qc.invalidateQueries({ queryKey: ["units"] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any, vars) => {
      const dependants = isForceable(e);
      if (dependants && !vars.force) {
        setForceTarget({ id: vars.id, dependants });
        return;
      }
      alert(e?.response?.data?.message ?? "Error");
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-2xl text-primary-800 flex items-center gap-2">
            <Building size={22} />
            {tr("Unidades", "Units")}
          </h1>
          <p className="text-sm text-slate-600">
            {tr("Catálogo de apartamentos del condominio.", "Condominium unit catalog.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setProvisioning(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-primary-700 text-primary-800 rounded hover:bg-primary-50"
            title={tr("Crea un usuario por cada unidad con correo del titular y sin usuario", "Creates a login for every unit with an owner email and no login")}
          >
            <UserPlus size={14} />
            {tr("Crear usuarios titulares", "Create owner logins")}
          </button>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800"
          >
            <Plus size={14} />
            {tr("Nueva unidad", "New unit")}
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          className="input max-w-xs"
          placeholder={tr("Buscar por unidad, código, titular o dirección…", "Search by unit, code, owner or address…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-xs text-slate-500">
          {visible.length} {tr("unidades", "units")}
          {withoutUser > 0 && (
            <>
              {" · "}
              <span className="text-amber-700">
                {withoutUser} {tr("sin usuario titular", "without an owner login")}
              </span>
            </>
          )}
          {withoutFee > 0 && (
            <>
              {" · "}
              <span className="text-amber-700" title={tr("No se les generan cuotas hasta que tengan una cuota mensual", "No fees are generated for them until they have a monthly fee")}>
                {withoutFee} {tr("sin cuota mensual", "without a monthly fee")}
              </span>
            </>
          )}
        </span>
      </div>

      <div className="bg-white rounded-lg shadow-elegant border border-sand-200 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-8 text-center text-slate-500">{tr("Cargando…", "Loading…")}</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{tr("Sin unidades.", "No units.")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-sand-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">{tr("Unidad", "Unit")}</th>
                <th className="text-left px-4 py-3">{tr("Edificio", "Building")}</th>
                <th className="text-left px-4 py-3">{tr("Hab.", "Beds")}</th>
                <th className="text-right px-4 py-3">{tr("Cuota mensual", "Monthly fee")}</th>
                <th className="text-left px-4 py-3">{tr("Titular", "Owner")}</th>
                <th className="text-left px-4 py-3">{tr("Contacto", "Contact")}</th>
                <th className="text-left px-4 py-3">{tr("Dirección USPS", "USPS address")}</th>
                <th className="text-left px-4 py-3">{tr("Usuario", "Login")}</th>
                <th className="text-right px-4 py-3">{tr("Acciones", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-primary-800">
                    <div>{u.unitNumber}</div>
                    <div className="text-xs font-mono font-normal text-slate-500">{u.code ?? deriveUnitCode(u.unitNumber, u.building)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.building ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{u.bedrooms ?? "—"}</td>
                  <td className="px-4 py-3 text-right text-slate-700 tabular-nums">
                    {u.monthlyFee != null ? formatCurrency(u.monthlyFee) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {u.ownerName}
                    {u.currentTenant && (
                      <div className="mt-1 text-xs text-slate-500">
                        <span className="mr-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-800">{tr("Alquilada", "Rented")}</span>
                        {tr("Inquilino", "Tenant")}: {u.currentTenant.name}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {u.ownerEmail && <div>{u.ownerEmail}</div>}
                    {u.ownerEmail2 && <div>{u.ownerEmail2}</div>}
                    {u.ownerPhone && <div>{u.ownerPhone}</div>}
                    {u.ownerPhone2 && <div>{u.ownerPhone2}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[16rem]">
                    {u.officialAddress ?? <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u._count ? (
                      u._count.users > 0 ? (
                        <span className="text-xs text-emerald-700">{u._count.users}</span>
                      ) : (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                          {tr("Sin usuario", "No login")}
                        </span>
                      )
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <AdminRecordActions
                      className="justify-end"
                      entityLabel={tr(`la unidad ${u.unitNumber}`, `unit ${u.unitNumber}`)}
                      onEdit={() => setEditing(u)}
                      onDelete={() => deleteMutation.mutate({ id: u.id })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ForceDeleteDialog
        open={!!forceTarget}
        dependants={forceTarget?.dependants ?? null}
        pending={deleteMutation.isPending}
        onCancel={() => setForceTarget(null)}
        onConfirm={() => forceTarget && deleteMutation.mutate({ id: forceTarget.id, force: true })}
      />
      <ProvisionOwnersModal
        open={provisioning}
        onClose={() => setProvisioning(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["units"] });
          qc.invalidateQueries({ queryKey: ["users"] });
        }}
        tr={tr}
      />
      {creating && (
        <UnitFormModal
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["units"] });
          }}
          tr={tr}
        />
      )}
      {editing && (
        <UnitFormModal
          target={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["units"] });
          }}
          tr={tr}
        />
      )}
    </div>
  );
}

function UnitFormModal({
  target,
  onClose,
  onDone,
  tr,
}: {
  target?: Unit;
  onClose: () => void;
  onDone: () => void;
  tr: (es: string, en: string) => string;
}) {
  const [form, setForm] = useState({
    unitNumber: target?.unitNumber ?? "",
    code: target?.code ?? "",
    officialAddress: target?.officialAddress ?? "",
    building: target?.building ?? "",
    bedrooms: target?.bedrooms != null ? String(target.bedrooms) : "",
    monthlyFee: target?.monthlyFee != null ? String(parseFloat(target.monthlyFee)) : "",
    ownerName: target?.ownerName ?? "",
    ownerEmail: target?.ownerEmail ?? "",
    ownerPhone: target?.ownerPhone ?? "",
    ownerEmail2: target?.ownerEmail2 ?? "",
    ownerPhone2: target?.ownerPhone2 ?? "",
    isOccupied: target?.isOccupied ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  // The code follows the unit number until the admin edits it by hand.
  const [codeTouched, setCodeTouched] = useState(
    !!target?.code && target.code !== deriveUnitCode(target.unitNumber, target.building)
  );
  const setIdentity = (patch: { unitNumber?: string; building?: string }) =>
    setForm((f) => {
      const next = { ...f, ...patch };
      if (!codeTouched) next.code = deriveUnitCode(next.unitNumber, next.building) ?? "";
      return next;
    });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        unitNumber: form.unitNumber,
        code: form.code || null,
        officialAddress: form.officialAddress || null,
        building: form.building || null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        monthlyFee: form.monthlyFee ? Number(form.monthlyFee) : null,
        ownerName: form.ownerName,
        ownerEmail: form.ownerEmail || null,
        ownerPhone: form.ownerPhone || null,
        ownerEmail2: form.ownerEmail2.trim() || null,
        ownerPhone2: form.ownerPhone2.trim() || null,
        isOccupied: form.isOccupied,
      };
      if (target) return (await api.put(`/units/${target.id}`, payload)).data;
      return (await api.post("/units", payload)).data;
    },
    onSuccess: onDone,
    onError: (err) => {
      setError(
        (err as AxiosError<{ message: string }>).response?.data?.message ??
          tr("No se pudo guardar.", "Could not save.")
      );
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={target ? tr("Editar unidad", "Edit unit") : tr("Nueva unidad", "New unit")}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{tr("Número", "Number")}</label>
            <input
              className="input"
              value={form.unitNumber}
              onChange={(e) => setIdentity({ unitNumber: e.target.value })}
              placeholder="E-1"
              required
            />
          </div>
          <div>
            <label className="label">{tr("Edificio", "Building")}</label>
            <input
              className="input"
              value={form.building}
              onChange={(e) => setIdentity({ building: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{tr("Código estándar", "Standard code")}</label>
            <input
              className="input font-mono"
              value={form.code}
              onChange={(e) => {
                setCodeTouched(true);
                setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }));
              }}
              placeholder="CD1-E-01"
            />
            <p className="text-xs text-slate-500 mt-1">
              {tr("Se genera del número de unidad; puede ajustarlo. Vacío = automático.", "Derived from the unit number; editable. Empty = automatic.")}
            </p>
          </div>
          <div>
            <label className="label">{tr("Dirección USPS", "USPS address")}</label>
            <input
              className="input"
              value={form.officialAddress}
              onChange={(e) => setForm((f) => ({ ...f, officialAddress: e.target.value }))}
              placeholder={tr("Ej. Calle 1 #123, San Juan PR 00901", "e.g. 123 Calle 1, San Juan PR 00901")}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{tr("Habitaciones", "Bedrooms")}</label>
            <input
              type="number"
              min={0}
              max={10}
              className="input"
              value={form.bedrooms}
              onChange={(e) => setForm((f) => ({ ...f, bedrooms: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">{tr("Cuota mensual (USD)", "Monthly fee (USD)")}</label>
            <input
              type="number"
              step="0.01"
              min={0}
              className="input"
              placeholder="285.00"
              value={form.monthlyFee}
              onChange={(e) => setForm((f) => ({ ...f, monthlyFee: e.target.value }))}
            />
            <p className="text-xs text-slate-500 mt-1">
              {tr("Se usa al generar las cuotas de cada período.", "Billed when each period's fees are generated.")}
            </p>
          </div>
        </div>
        <div>
          <label className="label">{tr("Titular", "Owner name")}</label>
          <input
            className="input"
            value={form.ownerName}
            onChange={(e) => setForm((f) => ({ ...f, ownerName: e.target.value }))}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{tr("Correo del titular", "Owner email")}</label>
            <input
              type="email"
              className="input"
              value={form.ownerEmail}
              onChange={(e) => setForm((f) => ({ ...f, ownerEmail: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">{tr("Teléfono del titular", "Owner phone")}</label>
            <input
              className="input"
              value={form.ownerPhone}
              onChange={(e) => setForm((f) => ({ ...f, ownerPhone: e.target.value }))}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">
              {tr("Segundo correo", "Second email")} <span className="text-slate-400 font-normal">({tr("opcional", "optional")})</span>
            </label>
            <input
              type="email"
              className="input"
              value={form.ownerEmail2}
              onChange={(e) => setForm((f) => ({ ...f, ownerEmail2: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">
              {tr("Segundo teléfono", "Second phone")} <span className="text-slate-400 font-normal">({tr("opcional", "optional")})</span>
            </label>
            <input
              className="input"
              value={form.ownerPhone2}
              onChange={(e) => setForm((f) => ({ ...f, ownerPhone2: e.target.value }))}
            />
          </div>
        </div>
        <p className="-mt-2 text-xs text-slate-500">
          {tr(
            "Comunicados, recordatorios y recibos se envían a ambos correos del titular.",
            "Announcements, reminders and receipts go to both of the owner's emails."
          )}
        </p>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.isOccupied}
            onChange={(e) => setForm((f) => ({ ...f, isOccupied: e.target.checked }))}
          />
          {tr("Unidad ocupada", "Unit is occupied")}
        </label>
        {target ? (
          <TenantSection unitId={target.id} tr={tr} />
        ) : (
          <p className="text-xs text-slate-500">
            {tr("Si la unidad está alquilada, podrá registrar al inquilino después de guardarla.", "If the unit is rented, you can register the tenant after saving it.")}
          </p>
        )}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded border-slate-300 hover:bg-slate-50">
            {tr("Cancelar", "Cancel")}
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.unitNumber || !form.ownerName}
            className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800 disabled:opacity-60"
          >
            {save.isPending ? tr("Guardando…", "Saving…") : tr("Guardar", "Save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The tenant living in the unit now, and the earlier ones. Registering a new tenant ends the
 * current lease on the new start date; ending a lease leaves the unit without a tenant.
 * Tenants receive announcements; fee emails go to the owner only.
 */
function TenantSection({ unitId, tr }: { unitId: string; tr: (es: string, en: string) => string }) {
  const { i18n } = useTranslation();
  const qc = useQueryClient();
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ["unit-tenants", unitId],
    queryFn: async () => (await api.get<Tenant[]>(`/units/${unitId}/tenants`)).data,
  });
  const current = tenants.find((t) => !t.endDate) ?? null;
  const past = tenants.filter((t) => t.endDate);
  const [editing, setEditing] = useState<Tenant | "new" | null>(null);
  const [ending, setEnding] = useState(false);
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [showPast, setShowPast] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["unit-tenants", unitId] });
    qc.invalidateQueries({ queryKey: ["units"] });
  };
  const endLease = useMutation({
    mutationFn: async () => (await api.post(`/units/${unitId}/tenants/${current!.id}/end`, { endDate })).data,
    onSuccess: () => {
      setEnding(false);
      refresh();
    },
  });
  const date = (d?: string | null) => (d ? formatDateOnly(d, i18n.language) : "");
  const contact = (t: Tenant) => [t.email, t.email2, t.phone, t.phone2].filter(Boolean).join(" · ");

  return (
    <fieldset className="space-y-2 rounded border border-slate-200 p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <KeyRound size={12} className="mr-1 inline" /> {tr("Inquilino", "Tenant")}
      </legend>
      {isLoading ? (
        <p className="text-sm text-slate-500">{tr("Cargando…", "Loading…")}</p>
      ) : editing ? (
        <TenantForm
          unitId={unitId}
          tenant={editing === "new" ? null : editing}
          replacing={editing === "new" && !!current}
          tr={tr}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      ) : current ? (
        <div className="space-y-2 text-sm">
          <div>
            <div className="font-medium text-slate-800">{current.name}</div>
            {contact(current) && <div className="text-xs text-slate-600">{contact(current)}</div>}
            <div className="text-xs text-slate-500">{tr("Desde", "Since")} {date(current.startDate)}</div>
            {current.notes && <div className="mt-1 whitespace-pre-line text-xs text-slate-500">{current.notes}</div>}
          </div>
          {ending ? (
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="label">{tr("Fin del arrendamiento", "Lease end")}</label>
                <input type="date" className="input" min={current.startDate.slice(0, 10)} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <button type="button" className="btn-primary text-sm" disabled={endLease.isPending} onClick={() => endLease.mutate()}>
                {tr("Confirmar", "Confirm")}
              </button>
              <button type="button" className="btn-secondary text-sm" onClick={() => setEnding(false)}>
                {tr("Cancelar", "Cancel")}
              </button>
              {endLease.isError && (
                <p className="w-full text-xs text-red-600">{(endLease.error as any)?.response?.data?.message ?? tr("No se pudo guardar.", "Could not save.")}</p>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-secondary text-xs" onClick={() => setEditing(current)}>{tr("Editar", "Edit")}</button>
              <button type="button" className="btn-secondary text-xs" onClick={() => setEnding(true)}>{tr("Finalizar arrendamiento", "End lease")}</button>
              <button type="button" className="btn-secondary text-xs" onClick={() => setEditing("new")}>{tr("Cambiar de inquilino", "New tenant")}</button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-slate-500">{tr("La unidad no está alquilada.", "The unit is not rented.")}</span>
          <button type="button" className="btn-secondary text-xs" onClick={() => setEditing("new")}>
            <Plus size={12} /> {tr("Registrar inquilino", "Register tenant")}
          </button>
        </div>
      )}
      {past.length > 0 && (
        <div className="border-t border-slate-100 pt-2">
          <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-primary" onClick={() => setShowPast((o) => !o)}>
            {showPast ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {tr("Inquilinos anteriores", "Earlier tenants")} ({past.length})
          </button>
          {showPast && (
            <ul className="mt-1 space-y-1 text-xs text-slate-600">
              {past.map((t) => (
                <li key={t.id}>
                  <span className="font-medium">{t.name}</span> · {date(t.startDate)} – {date(t.endDate)}
                  {contact(t) && <span className="text-slate-500"> · {contact(t)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </fieldset>
  );
}

function TenantForm({
  unitId,
  tenant,
  replacing,
  tr,
  onClose,
  onSaved,
}: {
  unitId: string;
  tenant: Tenant | null;
  replacing: boolean;
  tr: (es: string, en: string) => string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: tenant?.name ?? "",
    email: tenant?.email ?? "",
    email2: tenant?.email2 ?? "",
    phone: tenant?.phone ?? "",
    phone2: tenant?.phone2 ?? "",
    notes: tenant?.notes ?? "",
    startDate: (tenant?.startDate ?? new Date().toISOString()).slice(0, 10),
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, startDate: new Date(form.startDate).toISOString() };
      return tenant
        ? (await api.put(`/units/${unitId}/tenants/${tenant.id}`, body)).data
        : (await api.post(`/units/${unitId}/tenants`, body)).data;
    },
    onSuccess: onSaved,
  });
  return (
    <div className="space-y-2 text-sm">
      {replacing && (
        <p className="text-xs text-amber-700">
          {tr("El inquilino actual quedará en el historial, con fin en la fecha de inicio del nuevo.", "The current tenant moves to the history, ending on the new tenant's start date.")}
        </p>
      )}
      <div>
        <label className="label">{tr("Nombre del inquilino", "Tenant name")}</label>
        <input className="input" value={form.name} onChange={set("name")} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">{tr("Correo", "Email")}</label>
          <input type="email" className="input" value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label className="label">{tr("Segundo correo", "Second email")}</label>
          <input type="email" className="input" value={form.email2} onChange={set("email2")} />
        </div>
        <div>
          <label className="label">{tr("Teléfono", "Phone")}</label>
          <input className="input" value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <label className="label">{tr("Segundo teléfono", "Second phone")}</label>
          <input className="input" value={form.phone2} onChange={set("phone2")} />
        </div>
        <div>
          <label className="label">{tr("Inicio del arrendamiento", "Lease start")}</label>
          <input type="date" className="input" value={form.startDate} onChange={set("startDate")} />
        </div>
      </div>
      <div>
        <label className="label">{tr("Notas", "Notes")}</label>
        <textarea className="input min-h-[50px]" value={form.notes} onChange={set("notes")} />
      </div>
      {save.isError && (
        <p className="text-xs text-red-600">{(save.error as any)?.response?.data?.message ?? tr("No se pudo guardar.", "Could not save.")}</p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary text-sm" onClick={onClose}>{tr("Cancelar", "Cancel")}</button>
        <button type="button" className="btn-primary text-sm" disabled={save.isPending || !form.name.trim() || !form.startDate} onClick={() => save.mutate()}>
          {tr("Guardar inquilino", "Save tenant")}
        </button>
      </div>
    </div>
  );
}
